import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Bot, Cloud, Code, MessageSquare, BarChart3, Lightbulb, Cpu, CheckCircle } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const AnimatedSection: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.08 });
  return (
    <div
      ref={ref}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.7s cubic-bezier(.22,1,.36,1) ${delay}ms, transform 0.7s cubic-bezier(.22,1,.36,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

const services = [
  {
    icon: <Bot size={32} />,
    title: 'AI-Powered Workflow Automation',
    description: 'We design GenAI workflows that automate document processing, data entry, email handling, and back-office operations — freeing your team to focus on higher-value work that drives growth.',
    features: [
      'Invoice, contract & document processing',
      'Automated data entry & extraction',
      'Email triage & response drafting',
      'Custom AI agents for repetitive tasks',
    ],
    color: '#8B5CF6',
    gradient: 'linear-gradient(135deg, #8B5CF6 0%, #a855f7 100%)',
  },
  {
    icon: <MessageSquare size={32} />,
    title: 'AI Chatbots & Knowledge Assistants',
    description: 'Deploy AI chatbots and internal knowledge bases trained on your products, policies, and SOPs. Your customers get 24/7 support. Your staff gets instant answers. No additional headcount required.',
    features: [
      'Customer support chatbots',
      'Internal knowledge base assistants',
      'Lead qualification & capture bots',
      'FAQ & product recommendation engines',
    ],
    color: '#1E3A5F',
    gradient: 'linear-gradient(135deg, #1E3A5F 0%, #3B6B9B 100%)',
  },
  {
    icon: <Cloud size={32} />,
    title: 'Cloud Setup & Optimization',
    description: 'Our team applies the same AWS architecture principles they refined at Fortune 500 companies — right-sized for your business. Secure, cost-optimized infrastructure with no surprise bills and no wasted resources.',
    features: [
      'AWS setup & migration',
      'Cost optimization & right-sizing',
      'Security & compliance hardening',
      'Automated backups & monitoring',
    ],
    color: '#FF9900',
    gradient: 'linear-gradient(135deg, #FF9900 0%, #FF6600 100%)',
  },
  {
    icon: <Code size={32} />,
    title: 'Custom Business Applications',
    description: 'When off-the-shelf software falls short, we build custom web apps, customer portals, and internal tools tailored to your exact workflows — production-grade code your team can own and extend.',
    features: [
      'Customer portals & dashboards',
      'Internal operations tools',
      'Booking & scheduling systems',
      'Web apps & mobile-friendly interfaces',
    ],
    color: '#3b82f6',
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
  },
  {
    icon: <BarChart3 size={32} />,
    title: 'Data Dashboards & Reporting',
    description: 'We transform scattered spreadsheets and siloed data into clear, actionable dashboards. Your leadership team gets the metrics that matter — delivered through reporting systems built to scale.',
    features: [
      'Executive & operations dashboards',
      'Automated reporting pipelines',
      'Sales & marketing analytics',
      'Data cleanup & consolidation',
    ],
    color: '#22c55e',
    gradient: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
  },
  {
    icon: <Lightbulb size={32} />,
    title: 'AI Strategy & Roadmapping',
    description: 'We assess your workflows, identify the highest-ROI opportunities for AI adoption, and build a clear technology roadmap — grounded in real-world experience, not industry hype.',
    features: [
      'AI readiness assessment',
      'Workflow audit & opportunity mapping',
      'ROI-first technology roadmap',
      'Team training & change management',
    ],
    color: '#F59E0B',
    gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
  },
];

const processSteps = [
  { step: '01', title: 'Discovery', desc: 'We learn your business, your team, and your objectives. A focused conversation about where technology can deliver measurable impact.' },
  { step: '02', title: 'Architecture', desc: 'We design a solution right-sized for your budget — with clear milestones, transparent pricing, and a roadmap grounded in proven patterns.' },
  { step: '03', title: 'Build', desc: 'We develop iteratively with regular demos, keeping you involved at every stage. Transparent progress, no surprises.' },
  { step: '04', title: 'Deploy & Transfer', desc: 'We deploy to production, train your team to own the solution, and provide ongoing support. Full knowledge transfer is standard.' },
];

const Services: React.FC = () => {
  return (
    <div>
      {/* ─── Hero ─── */}
      <section
        className="hero-fade-in hero-padding-center"
        style={{
          background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 50%, #0F172A 100%)',
          borderRadius: '24px',
          marginBottom: '64px',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(245,158,11,0.06)', filter: 'blur(60px)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 16px',
              background: 'rgba(245,158,11,0.15)',
              color: '#F59E0B',
              borderRadius: '9999px',
              fontSize: '0.875rem',
              fontWeight: 600,
              marginBottom: '20px',
              border: '1px solid rgba(245,158,11,0.2)',
            }}
          >
            <Cpu size={14} /> What We Do
          </span>
          <h1
            style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 800,
              fontFamily: 'var(--font-heading)',
              color: '#fff',
              marginBottom: '16px',
            }}
          >
            Our Services
          </h1>
          <p
            style={{
              fontSize: '1.15rem',
              color: 'rgba(255,255,255,0.6)',
              lineHeight: 1.7,
              maxWidth: '560px',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            Enterprise-grade AI and cloud engineering — delivered by a team whose experience
            spans Fortune 500 companies and government agencies, at a pace and price point that works for growing businesses.
          </p>
        </div>
      </section>

      {/* ─── Services Grid ─── */}
      <section style={{ marginBottom: '80px' }}>
        <div className="grid-cards">
          {services.map((service, i) => (
            <AnimatedSection key={i} delay={i * 100}>
              <div
                style={{
                  background: '#fff',
                  borderRadius: '20px',
                  overflow: 'hidden',
                  border: '1px solid rgba(0,0,0,0.06)',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
                  transition: 'all 0.3s cubic-bezier(.22,1,.36,1)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-6px)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.03)';
                }}
              >
                <div style={{ height: '6px', background: service.gradient }} />
                <div style={{ padding: '32px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div
                    style={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '16px',
                      background: `${service.color}10`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: service.color,
                      marginBottom: '20px',
                    }}
                  >
                    {service.icon}
                  </div>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: '12px' }}>{service.title}</h3>
                  <p style={{ color: 'var(--color-gray-500)', lineHeight: 1.7, fontSize: '0.95rem', marginBottom: '20px' }}>
                    {service.description}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: 'auto' }}>
                    {service.features.map((feature, j) => (
                      <div key={j} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CheckCircle size={16} style={{ color: service.color, flexShrink: 0 }} />
                        <span style={{ fontSize: '0.88rem', color: 'var(--color-gray-600)' }}>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* ─── Our Process ─── */}
      <AnimatedSection>
        <section
          className="section-padding-lg"
          style={{
            background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
            borderRadius: '24px',
            marginBottom: '64px',
          }}
        >
          <div className="text-center" style={{ marginBottom: '48px' }}>
            <span
              style={{
                display: 'inline-block',
                padding: '6px 16px',
                background: 'rgba(30,58,95,0.08)',
                color: 'var(--color-primary)',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: 600,
                marginBottom: '16px',
              }}
            >
              How We Work
            </span>
            <h2 className="text-3xl font-bold">Our Process</h2>
          </div>

          <div className="grid-4-col">
            {processSteps.map((item, i) => (
              <AnimatedSection key={i} delay={i * 120}>
                <div style={{ textAlign: 'center' }}>
                  <div
                    style={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #1E3A5F, #0F172A)',
                      color: '#F59E0B',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.1rem',
                      fontWeight: 800,
                      fontFamily: 'var(--font-heading)',
                      margin: '0 auto 16px',
                    }}
                  >
                    {item.step}
                  </div>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '8px' }}>{item.title}</h3>
                  <p style={{ color: 'var(--color-gray-500)', fontSize: '0.9rem', lineHeight: 1.6 }}>{item.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </section>
      </AnimatedSection>

      {/* ─── CTA ─── */}
      <AnimatedSection>
        <section
          className="section-padding"
          style={{
            background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 100%)',
            borderRadius: '24px',
            marginBottom: '32px',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(245,158,11,0.08)', filter: 'blur(40px)' }} />
          <h2 style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', fontWeight: 800, color: '#fff', marginBottom: '16px', fontFamily: 'var(--font-heading)', position: 'relative' }}>
            Ready to Modernize Your Business with AI?
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.1rem', marginBottom: '32px', maxWidth: '500px', marginLeft: 'auto', marginRight: 'auto', position: 'relative' }}>
            Our team has spent over a decade delivering production systems at the highest levels. Let us bring that same discipline to your next project. Free consultation, no commitment required.
          </p>
          <Link
            to="/contact"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '14px 32px',
              background: 'linear-gradient(135deg, #F59E0B, #D97706)',
              color: '#0F172A',
              borderRadius: '12px',
              fontWeight: 700,
              fontSize: '1rem',
              textDecoration: 'none',
              transition: 'transform 0.25s, box-shadow 0.25s',
              boxShadow: '0 4px 15px rgba(245,158,11,0.35)',
              position: 'relative',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(245,158,11,0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(245,158,11,0.35)';
            }}
          >
            Get a Quote <ArrowRight size={16} />
          </Link>
        </section>
      </AnimatedSection>
    </div>
  );
};

export default Services;
